import { Combobox, createListCollection, Dialog, Tabs, useAsyncList } from "@ark-ui/solid";
import posthog from "posthog-js";
import Wizard from "../../components/modal/Wizard";
import type { TranslationKey } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import {
  batch,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  createUniqueId,
  For,
  getOwner,
  Index,
  Match,
  type ParentProps,
  runWithOwner,
  Show,
  Switch,
  untrack,
} from "solid-js";
import { Icon, type IconName } from "../../components/icon/Icon";
import Text from "../../components/text/Text";
import TextAreaInput from "../../components/inputs/TextAreaInput";
import { MAX_CHARACTERS_MD, MAX_NUMBER_XL, MAX_NUMBER_XL_NEGATIVE, USER_INPUT_DEBOUNCE } from "../../constants";
import { SingleSelect, SingleSelectInput } from "../../components/menu/Select";
import type { SelectOption } from "../../types/Menu";
import { CompactDateInput, CompactDateTimeInput, DateTimeInput } from "../../components/inputs/DateInput";
import { debounce } from "@solid-primitives/scheduled";
import type { VariableConfig } from "../../fields/types";
import toVariableDefinition from "../../variables/toVariableDefinition";
import { CompactTextInput } from "../../components/inputs/TextInput";
import type { MapApi } from "../../components/map/Map";
import LocationMap, { DEFAULT_ZOOM, MAX_ZOOM, MIN_ZOOM } from "../../components/map/Map";
import type { LocationResult, MapStyle, OpenCageResult } from "../../types/Location";
import type { BaseVariableDefinition, BaseVariableType } from "../../variables/Variable";
import { first, isEmpty, isNil } from "lodash-es";
import { CompactNumberInput } from "../../components/inputs/NumberInput";
import { IconButton, TextButton } from "../../components/button/Button";
import { MultiSelectWithSearchInput } from "../../components/menu/MultiSelectWithSearch";
import type { User } from "../../state/user";
import { groupAvatar, toSelectOption } from "../../utils/avatarUtil";
import { ErrorMessage, Header } from "../../components/inputs/Input";
import { array, check, nonEmpty, object, picklist, pipe, record, safeParse, string, unknown } from "valibot";
import { FloatingSearchBar } from "../../components/searchBar/SearchBar";
import {
  coordinatesToHumanReadableString,
  LocationProvider,
  normalizeLongitude,
  openCageToLocationResult,
} from "../../utils/locationUtil";
import type { SearchResult } from "leaflet-geosearch/dist/providers/provider.js";
import TextListItem from "../../components/menu/listItems/TextListItem";
import type { PublishInfoType } from "../../services/ApiService";
import ApiService from "../../services/ApiService";
import type FormId from "../../state/FormId";
import type FormVersionId from "../../state/FormVersionId";
import { hours } from "../../utils/timeUtil";
import { useCurrentModal, useModalManager } from "../../components/modal/ModalManager";
import GenericFailureModal from "../GenericFailureModal";
import { Translation } from "../../utils/Translation";
import InlineMessage from "../../components/inlineMessage/InlineMessage";
import { condition } from "../../utils/solidUtils";
import { DEFAULT_DATE_TIME_FORMAT } from "../../components/formatting/FormatDate";
import type { Group } from "../../state/group";
import { resolveName as resolveGroupName } from "../../utils/groupUtil";
import { noop } from "../../utils/noop";
import { toTimestamp } from "../../utils/dateUtil";
import { strippedIsoDateTime } from "../../utils/validation/base";
import { makeWindowEventListener } from "../../utils/makeWindowEventListener";
import { makeEventListener } from "@solid-primitives/event-listener";
import { keyFromSelector } from "i18next";
import type { TaskRecipient } from "../../types/Task";
import FeatureService from "../../services/FeatureService";
import { DynamicValueIconTile } from "../../components/icon/IconTile";
import { createForm } from "../../components/form/createForm";
import FormVersionPreview from "../formEditor/preview/FormVersionPreview.tsx";
import { toForm } from "../../utils/mappers/form.ts";
import { toFormVersion } from "../../utils/mappers/formVersion.ts";
import { POSTHOG_EVENTS } from "../../services/PostHogService";

const MIN_QUERY_LENGTH = 2;

type CreateTaskWizardProps = {
  users: User[];
  groups: Group[];
  formVariables: VariableConfig[];
  formId: FormId;
  formVersionId: FormVersionId;
};

type FormData = Record<string, unknown>;

type TaskFormValues = {
  recipients: TaskRecipient[];
  message: string;
  sendTask: SendTask;
  schedule: string;
  dueOn: string;
  location: LocationResult | null;
  formData: FormData;
  variables: Record<string, VariableValue>;
};

const getVariableError = (variable: VariableConfig, value: VariableValue) =>
  first(
    (safeParse(toVariableDefinition(variable.type).schema, { ...variable, defaultValue: value }).issues ?? []).map(
      (issue) => issue.message,
    ),
  );

const buildTaskSchema = (formVariables: VariableConfig[]) =>
  pipe(
    object({
      recipients: pipe(array(unknown()), nonEmpty()),
      message: string(),
      sendTask: picklist<SendTask[]>(["IMMEDIATE", "IN_ONE_HOUR", "IN_TWO_HOURS", "SCHEDULE"]),
      schedule: string(),
      dueOn: string(),
      location: unknown(),
      formData: record(string(), unknown()),
      variables: pipe(
        record(string(), unknown()),
        check((variables) =>
          formVariables.every((variable) =>
            isNil(getVariableError(variable, variables[variable.uid.toString()] as VariableValue)),
          ),
        ),
      ),
    }),
    check(({ sendTask, schedule }) => sendTask !== "SCHEDULE" || !isEmpty(schedule)),
    check(({ schedule }) => isEmpty(schedule) || safeParse(strippedIsoDateTime(), schedule).success),
    check(({ dueOn }) => isEmpty(dueOn) || safeParse(strippedIsoDateTime(), dueOn).success),
  );

const toPublishInfo = ({
  type,
  value,
}: {
  type: SendTask;
  value: string;
}): { type: PublishInfoType; value: number } => {
  switch (type) {
    case "IMMEDIATE":
      return { type: "IMMEDIATE", value: 0 };
    case "IN_ONE_HOUR":
      return { type: "RELATIVE", value: hours(1) };
    case "IN_TWO_HOURS":
      return { type: "RELATIVE", value: hours(2) };
    case "SCHEDULE":
      return { type: "ABSOLUTE", value: toTimestamp(value) };
  }
};

const CreateTaskWizard = (props: CreateTaskWizardProps) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const { close, setBlockClose, setOnCloseBlocked } = useCurrentModal();
  const createTaskMutation = ApiService.createTask();

  const [locationTabVisited, setLocationTabVisited] = createSignal<boolean>(false);

  const form = createForm<TaskFormValues>(
    buildTaskSchema(untrack(() => props.formVariables)),
    async (output) => {
      const variables = untrack(() => props.formVariables).reduce<FormData>((acc, variable) => {
        acc[variable.dataName] = output.variables[variable.uid.toString()];
        return acc;
      }, {});

      const informationDate = isEmpty(output.dueOn.trim()) ? undefined : toTimestamp(output.dueOn);

      try {
        await createTaskMutation.mutateAsync({
          formId: props.formId,
          recipients: output.recipients,
          message: output.message,
          publishInfo: toPublishInfo({ type: output.sendTask, value: output.schedule }),
          informationDate,
          location: output.location,
          registrationData: { ...output.formData, ...variables },
        });
        posthog.capture(POSTHOG_EVENTS.TASK_CREATED);
      } catch (e) {
        replace({
          content: () => <GenericFailureModal error={e} />,
        });
      }
      replace({
        content: () => <SuccessModal schedule={output.schedule} sendTask={output.sendTask} />,
      });
    },
    {
      recipients: [],
      message: "",
      sendTask: "IMMEDIATE",
      schedule: "",
      dueOn: "",
      location: null,
      formData: {},
      variables: untrack(() => props.formVariables).reduce<Record<string, VariableValue>>((acc, variable) => {
        acc[variable.uid.toString()] = variable.defaultValue as VariableValue;
        return acc;
      }, {}),
    },
  );

  const variableItems = createMemo<Record<string, VariableItem>>(() =>
    props.formVariables.reduce<Record<string, VariableItem>>((acc, variable) => {
      const uid = variable.uid.toString();
      const currentValue = (form.getValue("variables") ?? {})[uid] as VariableValue;
      acc[uid] = {
        definition: toVariableDefinition(variable.type),
        variable,
        currentValue,
        error: getVariableError(variable, currentValue),
      };
      return acc;
    }, {}),
  );

  const dueOnError = createMemo(() => {
    const value = form.getValue("dueOn");
    if (isEmpty(value)) {
      return undefined;
    }

    return first((safeParse(strippedIsoDateTime(), value).issues ?? []).map((issue) => issue.message));
  });

  const scheduleError = createMemo(() => {
    const value = form.getValue("schedule");
    if (isEmpty(value)) {
      return undefined;
    }

    return first((safeParse(strippedIsoDateTime(), value).issues ?? []).map((issue) => issue.message));
  });

  createEffect(() => {
    setBlockClose(form.hasUnsavedChanges());
  });

  makeEventListener(window, "beforeunload", (event) => {
    if (form.hasUnsavedChanges()) {
      event.preventDefault();
    }
  });

  setOnCloseBlocked(() => {
    if (window.confirm(t(($) => $.CREATE_TASK_DISCARD_MODAL_DESCRIPTION))) {
      close();
    }
  });

  return (
    <Wizard
      title={t(($) => $.CREATE_TASK_MODAL_TITLE)}
      content={() => (
        <Dialog.Context>
          {() => (
            <Tabs.Root
              orientation="vertical"
              defaultValue="recipients"
              onValueChange={(details) => {
                if (details.value === "location") {
                  setLocationTabVisited(true);
                }
              }}
              class="flex size-full"
            >
              <Tabs.List class="flex h-full w-64 flex-none shrink-0 flex-col items-start gap-0.5 border-r border-subtle px-2 py-4">
                <For each={buildMenuItems(isEmpty(props.formVariables))}>
                  {(menuItem) => (
                    <Tabs.Trigger
                      value={menuItem.view}
                      class="group/item flex w-full gap-2.5 rounded-sm px-2.5 py-2 outline-offset-0 hover:bg-surface-extra-subtle-hover focus-visible:outline-default selected:bg-surface-active"
                    >
                      <Icon
                        name={menuItem.icon}
                        size="lg"
                        type="solid"
                        class="shrink-0 icon-tertiary group-selected/item:icon-secondary"
                      />
                      <div class="flex flex-col items-start">
                        <Text text={t(menuItem.label)} truncate class="text-base-medium text-primary" />
                        <Show when={menuItem.isOptional}>
                          <Text
                            text={t(($) => $.CREATE_TASK_MENU_LABEL_OPTIONAL)}
                            truncate
                            class="text-base-regular text-secondary"
                          />
                        </Show>
                      </div>
                    </Tabs.Trigger>
                  )}
                </For>
              </Tabs.List>
              <div class="relative w-full min-w-0 flex-1 overflow-y-auto px-6 py-4">
                <Tabs.Content value="recipients">
                  <RecipientsTab
                    users={props.users}
                    groups={props.groups}
                    recipients={form.getValue("recipients") ?? []}
                    setRecipients={(value) => form.updateValue("recipients", value)}
                    recipientsMessageValue={form.getValue("message") ?? ""}
                    setRecipientsMessageValue={(value) => form.updateValue("message", value)}
                  />
                </Tabs.Content>
                <Tabs.Content value="planning">
                  <PlanningTab
                    sendTask={form.getValue("sendTask") ?? "IMMEDIATE"}
                    setSendTask={(value) => form.updateValue("sendTask", value)}
                    schedule={form.getValue("schedule") ?? ""}
                    setSchedule={(value) => form.updateValue("schedule", value)}
                    scheduleError={scheduleError()}
                    dueOn={form.getValue("dueOn") ?? ""}
                    setDueOn={(value) => form.updateValue("dueOn", value)}
                    dueOnError={dueOnError()}
                  />
                </Tabs.Content>
                <Tabs.Content value="location">
                  {/* Lazy-loading is only needed here to correctly render the map */}
                  <Show when={locationTabVisited()}>
                    <LocationTab
                      selectedSearchResult={form.getValue("location") ?? null}
                      setSelectedSearchResult={(value) => form.updateValue("location", value)}
                    />
                  </Show>
                </Tabs.Content>
                <Tabs.Content value="pre-fill">
                  <PreFillTab
                    formId={props.formId}
                    formVersionId={props.formVersionId}
                    setFormData={(value) => form.updateValue("formData", value)}
                    setInitialData={(value) =>
                      batch(() => {
                        form.updateValue("formData", value);
                        form.setInitialValue("formData", value);
                      })
                    }
                  />
                </Tabs.Content>
                <Tabs.Content value="variables">
                  <VariablesTab
                    formVariables={variableItems()}
                    onUpdateValue={(uid, value) =>
                      form.updateValue("variables", { ...(form.getValue("variables") ?? {}), [uid]: value })
                    }
                    onReset={(uid) => {
                      const variable = props.formVariables.find((v) => v.uid.toString() === uid);
                      if (isNil(variable)) {
                        return;
                      }
                      form.updateValue("variables", {
                        ...(form.getValue("variables") ?? {}),
                        [uid]: variable.defaultValue as VariableValue,
                      });
                    }}
                  />
                </Tabs.Content>
              </div>
            </Tabs.Root>
          )}
        </Dialog.Context>
      )}
      footer={{
        confirm: {
          label: keyFromSelector(($) => $.CREATE_BUTTON),
          action: form.submit,
          disabled: form.submitDisabled(),
        },
        showCloseButton: true,
      }}
    />
  );
};

type VariableItemCardProps = ParentProps<{
  variableItem: VariableItem;
  inputId: string;
  onReset: () => void;
}>;

const VariableItemCard = (props: VariableItemCardProps) => {
  const { t } = useTranslation();
  return (
    <li aria-label={props.variableItem.variable.name} class="flex flex-col gap-1 rounded-sm border border-subtle p-3">
      <div class="flex gap-1.5">
        <DynamicValueIconTile
          icon={props.variableItem.definition.icon}
          iconClassList={{ "bg-surface-subtle icon-primary": true }}
          size="sm"
        />
        <Header id={props.inputId} label={props.variableItem.variable.name} />
      </div>
      <div class="flex items-end gap-1">
        <span class="grow">{props.children}</span>
        <TextButton
          text={t(($) => $.CREATE_TASK_VARIABLES_RESET_BUTTON_LABEL)}
          variant="secondary"
          disabled={props.variableItem.currentValue === props.variableItem.variable.defaultValue}
          onClick={props.onReset}
        />
      </div>
      <ErrorMessage errorMessage={props.variableItem.error} />
    </li>
  );
};

type RecipientsTabProps = {
  users: User[];
  groups: Group[];
  recipients: TaskRecipient[];
  setRecipients: (recipients: TaskRecipient[]) => void;
  recipientsMessageValue: string;
  setRecipientsMessageValue: (value: string) => void;
};

const RecipientsTab = (props: RecipientsTabProps) => {
  const { t } = useTranslation();
  return (
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <Text as="h2" class="text-header-lg text-primary" text={t(($) => $.CREATE_TASK_RECIPIENTS_TITLE)} />
        <Text as="p" class="text-base-regular text-secondary" text={t(($) => $.CREATE_TASK_RECIPIENTS_DESCRIPTION)} />
      </div>
      <Show
        when={!(isEmpty(props.users) && isEmpty(props.groups))}
        fallback={<InlineMessage variant="warning" label={t(($) => $.CREATE_TASK_NO_USER_GROUPS_WARNING)} />}
      >
        <MultiSelectWithSearchInput<TaskRecipient>
          label={t(($) => $.CREATE_TASK_RECIPIENTS_TITLE)}
          onSelect={props.setRecipients}
          options={{
            [t(($) => $.SELECT_TAB_TITLE_USERS)]: props.users.map(toSelectOption),
            [t(($) => $.SELECT_TAB_TITLE_GROUPS)]: props.groups.map((group) => ({
              variant: "avatar",
              ...groupAvatar(group),
              label: resolveGroupName(group),
              value: { kind: "group", id: group.id },
            })),
          }}
          value={[]}
          required
          width="matchTrigger"
        />
      </Show>
      <TextAreaInput
        label={t(($) => $.CREATE_TASK_RECIPIENTS_MESSAGE_TITLE)}
        value={props.recipientsMessageValue}
        onBlur={({ currentTarget }) => props.setRecipientsMessageValue(currentTarget.value)}
        maxLength={MAX_CHARACTERS_MD}
        info={{
          title: t(($) => $.CREATE_TASK_RECIPIENTS_MESSAGE_HELP_TITLE),
          description: t(($) => $.CREATE_TASK_RECIPIENTS_MESSAGE_HELP_DESCRIPTION),
          alt: t(($) => $.CREATE_TASK_RECIPIENTS_MESSAGE_HELP_ALT),
        }}
      />
    </div>
  );
};

type PlanningTabProps = {
  sendTask: SendTask;
  setSendTask: (value: SendTask) => void;
  schedule: string;
  setSchedule: (value: string) => void;
  scheduleError: string | undefined;
  dueOn: string;
  setDueOn: (value: string) => void;
  dueOnError: string | undefined;
};

const PlanningTab = (props: PlanningTabProps) => {
  const { t } = useTranslation();

  const sendTaskOptions = createMemo<SelectOption<SendTask>[]>(() => [
    {
      variant: "text",
      label: t(($) => $.CREATE_TASK_PLANNING_SEND_TASK_IMMEDIATELY),
      value: "IMMEDIATE",
    },
    {
      variant: "text",
      label: t(($) => $.CREATE_TASK_PLANNING_SEND_TASK_IN_ONE_HOUR),
      value: "IN_ONE_HOUR",
    },
    {
      variant: "text",
      label: t(($) => $.CREATE_TASK_PLANNING_SEND_TASK_IN_TWO_HOURS),
      value: "IN_TWO_HOURS",
    },
    {
      variant: "text",
      label: t(($) => $.CREATE_TASK_PLANNING_SEND_TASK_SCHEDULE),
      value: "SCHEDULE",
    },
  ]);

  return (
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <Text as="h2" class="text-header-lg text-primary" text={t(($) => $.CREATE_TASK_PLANNING_TITLE)} />
        <Text as="p" class="text-base-regular text-secondary" text={t(($) => $.CREATE_TASK_PLANNING_DESCRIPTION)} />
      </div>
      <SingleSelectInput<SendTask>
        options={sendTaskOptions()}
        value={props.sendTask}
        onSelect={props.setSendTask}
        label={t(($) => $.CREATE_TASK_PLANNING_SEND_TASK_LABEL)}
        sameWidth
      />
      <Show when={props.sendTask === "SCHEDULE"}>
        <DateTimeInput
          ariaLabel={t(($) => $.CREATE_TASK_PLANNING_SEND_TASK_SCHEDULE)}
          value={props.schedule}
          onInput={(e) => {
            props.setSchedule(e.target.value);
          }}
          onBlur={(e) => props.setSchedule(e.target.value)}
          label={t(($) => $.CREATE_TASK_PLANNING_SEND_TASK_SCHEDULE)}
          errorMessage={props.scheduleError}
          required
        />
      </Show>
      <DateTimeInput
        ariaLabel={t(($) => $.CREATE_TASK_PLANNING_SEND_DUE_ON_LABEL)}
        value={props.dueOn}
        onInput={(e) => {
          props.setDueOn(e.target.value);
        }}
        onBlur={(e) => props.setDueOn(e.target.value)}
        label={t(($) => $.CREATE_TASK_PLANNING_SEND_DUE_ON_LABEL)}
        errorMessage={props.dueOnError}
      />
    </div>
  );
};

type LocationTabProps = {
  selectedSearchResult: LocationResult | null;
  setSelectedSearchResult: (result: LocationResult | null) => void;
};

const LocationTab = (props: LocationTabProps) => {
  const { t, currentLanguage } = useTranslation();

  const [map, setMap] = createSignal<MapApi | undefined>(undefined);

  const provider = new LocationProvider();

  const [inputText, setInputText] = createSignal("");
  const currentZoom = createMemo(() => map()?.zoom() ?? DEFAULT_ZOOM);

  const selectSearchResult = (result: LocationResult) => {
    props.setSelectedSearchResult(result);
    setInputText("");

    if (!isNil(result.coordinates)) {
      map()?.setLocation(result.coordinates);
      map()?.setMarker(result.coordinates);
    }
  };

  const clearSearchResult = () => {
    props.setSelectedSearchResult(null);
    setInputText("");

    map()?.clearMarker();
  };

  const list = useAsyncList<LocationResult>({
    initialFilterText: "",
    load: async ({ filterText, signal }) => {
      const query = filterText.trim();
      if (query.length < MIN_QUERY_LENGTH) {
        return { items: [] };
      }

      const raw = await provider.search({ query });

      if (signal?.aborted === true) {
        return { items: [] };
      }

      return {
        items: raw.map((r) => openCageToLocationResult(r as unknown as SearchResult<OpenCageResult>)),
      };
    },
  });

  const debouncedSetFilter = debounce((query: string) => {
    list().setFilterText(query);
  }, USER_INPUT_DEBOUNCE);

  const collection = createMemo(() =>
    createListCollection({
      items: list().items,
      itemToString: (item) => item.formattedValue ?? "",
      itemToValue: (item) =>
        `${item.formattedValue ?? ""}:${item.coordinates?.latitude ?? ""},${item.coordinates?.longitude ?? ""}`,
    }),
  );

  return (
    <div class="absolute inset-0 isolate">
      <div class="absolute inset-0 z-0">
        <LocationMap
          ref={setMap}
          onMapClick={(coords) => {
            // Leaflet repeats the map on the longitude, which could result in out of bounds values.
            const normalizedCoords = { latitude: coords.latitude, longitude: normalizeLongitude(coords.longitude) };
            ApiService.getReverseLocation(normalizedCoords).then((searchResult) =>
              untrack(() => selectSearchResult(searchResult)),
            );
          }}
        />
      </div>
      <div class="absolute inset-x-4 top-4 shadow-elevation-light-3">
        <Show
          when={props.selectedSearchResult}
          fallback={
            <Combobox.Root<LocationResult>
              onFocusOutside={() => {
                list().clearFilter();
              }}
              collection={collection()}
              inputValue={inputText()}
              onInputValueChange={({ inputValue }) => {
                setInputText(inputValue);
                debouncedSetFilter(inputValue);
              }}
              onValueChange={({ items }) => {
                const selected = items[0];
                if (isNil(selected)) {
                  return;
                }
                selectSearchResult(selected);
                setInputText("");
                list().clearFilter();
              }}
              positioning={{
                placement: "bottom-start",
                sameWidth: true,
                gutter: 0,
              }}
            >
              <Combobox.Control>
                <Combobox.Input
                  asChild={(htmlProps) => {
                    const hasResult = createMemo(() => list().loading || !isEmpty(list().items));
                    return (
                      <div
                        class="bg-surface-default"
                        classList={{ rounded: !hasResult(), "rounded-b-none": hasResult() }}
                      >
                        <FloatingSearchBar
                          onQueryChange={noop}
                          {...htmlProps}
                          placeholder={t(($) => $.CREATE_TASK_LOCATION_SEARCH_PLACEHOLDER)}
                          query={inputText()}
                          isActive={hasResult()}
                        />
                      </div>
                    );
                  }}
                />
              </Combobox.Control>
              <Combobox.Positioner>
                <Combobox.Content
                  class="z-20 h-82 overflow-y-auto border-t border-subtle bg-surface-default p-1 shadow-elevation-light-3"
                  aria-label={t(($) => $.CREATE_TASK_LOCATION_SEARCH_MENU_ARIA)}
                >
                  <Show
                    when={!list().loading}
                    fallback={
                      <Icon name="spinner" size="2xl" type="solid" class="mx-auto mt-10 animate-spin icon-secondary" />
                    }
                  >
                    <For each={list().items}>
                      {(item) => (
                        <Combobox.Item item={item} class="group">
                          <TextListItem label={item.formattedValue ?? ""} htmlProps={null} />
                        </Combobox.Item>
                      )}
                    </For>
                  </Show>
                </Combobox.Content>
              </Combobox.Positioner>
            </Combobox.Root>
          }
        >
          {(searchResult) => (
            <div class="flex w-full min-w-0 overflow-hidden rounded-sm">
              <button
                data-testid="searchResultButton"
                onClick={() => {
                  const coordinates = searchResult().coordinates;
                  if (!isNil(map()) && !isNil(coordinates)) {
                    map()!.setLocation(coordinates);
                  }
                }}
                class="flex h-13 min-w-0 grow gap-3 overflow-hidden bg-surface-default p-2 text-left hover:bg-surface-hover active:bg-surface-pressed"
              >
                <div class="flex size-9 min-w-0 shrink-0 items-center justify-center rounded-sm bg-accent-subtle-red">
                  <Icon name="locationMarker" size="lg" type="solid" class="icon-accent-red" />
                </div>
                <div class="flex min-w-0 flex-col">
                  <Text class="min-w-0 grow text-base-medium" truncate text={searchResult().formattedValue ?? ""} />
                  <Show when={searchResult().coordinates}>
                    {(coordinates) => (
                      <Text
                        class="min-w-0 grow text-small-medium text-secondary"
                        truncate
                        text={coordinatesToHumanReadableString(coordinates(), currentLanguage())}
                      />
                    )}
                  </Show>
                </div>
              </button>
              <button
                onClick={() => clearSearchResult()}
                aria-label={t(($) => $.CREATE_TASK_LOCATION_CLEAR_ARIA)}
                class="h-13 shrink-0 border-l border-subtle bg-surface-default px-3 hover:bg-surface-hover active:bg-surface-pressed"
              >
                <Icon name="x" size="lg" type="solid" class="icon-primary" />
              </button>
            </div>
          )}
        </Show>
      </div>
      <div class="absolute right-4 bottom-4 z-10 flex flex-col gap-2">
        <IconButton
          icon="gpsLocation"
          aria-label={t(($) => $.CREATE_TASK_LOCATION_CURRENT_ARIA)}
          size="large"
          onClick={() => map()?.recenter()}
          variant="secondary"
          class="shadow-elevation-light-3"
        />
        <div class="shadow-elevation-light-3">
          <IconButton
            icon="plus"
            aria-label={t(($) => $.ZOOM_IN)}
            size="large"
            variant="secondary"
            classList={{ "rounded-b-none": true }}
            onClick={() => map()?.zoomIn()}
            disabled={currentZoom() === MAX_ZOOM}
          />
          <IconButton
            icon="minus"
            aria-label={t(($) => $.ZOOM_OUT)}
            size="large"
            variant="secondary"
            classList={{ "rounded-t-none border-t-0": true }}
            onClick={() => map()?.zoomOut()}
            disabled={currentZoom() === MIN_ZOOM}
          />
        </div>
      </div>
      <Tabs.Root
        defaultValue="STREET"
        class="absolute bottom-4 left-4 z-10 shadow-elevation-light-3"
        onValueChange={(details) => map()?.setMapStyle(details.value as MapStyle)}
      >
        <Tabs.List class="group relative isolate flex h-9.5 items-center justify-center rounded-sm border border-subtle bg-surface-extra-subtle">
          <div class="flex gap-x-0.5">
            <Tabs.Trigger
              value="STREET"
              class="z-10 flex h-9.5 items-center justify-center gap-x-1.5 rounded-sm px-5 py-2.5 text-base-medium text-button-text-tertiary-soft select-none hover:text-button-text-tertiary-soft-hover focus-visible:outline-default selected:text-primary"
            >
              <span class="text-button">{t(($) => $.CREATE_TASK_LOCATION_STYLE_STREET)}</span>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="SATELLITE"
              class="z-10 flex h-9.5 items-center justify-center gap-x-1.5 rounded-sm p-2.5 text-base-medium text-button-text-tertiary-soft select-none hover:text-button-text-tertiary-soft-hover focus-visible:outline-default selected:text-primary"
            >
              <span class="text-button">{t(($) => $.CREATE_TASK_LOCATION_STYLE_SATELLITE)}</span>
            </Tabs.Trigger>
          </div>
          <Tabs.Indicator
            class="absolute rounded-sm border border-default bg-surface-default"
            style={{ width: "var(--width)", height: "var(--height)" }}
          />
        </Tabs.List>
      </Tabs.Root>
    </div>
  );
};

type PreFillTabProps = {
  formId: FormId;
  formVersionId: FormVersionId;
  setFormData: (value: FormData) => void;
  setInitialData: (value: FormData) => void;
};

const FormDataChangedEventSchema = object({
  type: string("formDataChanged"),
  data: record(string(), unknown()),
});

const PreFillTab = (props: PreFillTabProps) => {
  const { t } = useTranslation();
  const owner = getOwner();

  let iframeRef!: HTMLIFrameElement;
  let hasReceivedInitialFormData = false;

  makeWindowEventListener(
    () => iframeRef.contentWindow,
    FormDataChangedEventSchema,
    ({ data }) => {
      untrack(() => {
        if (hasReceivedInitialFormData) {
          props.setFormData(data);
        } else {
          hasReceivedInitialFormData = true;
          props.setInitialData(data);
        }
      });
    },
  );

  const [form] = createResource(() =>
    ApiService.getForm(props.formId).then((formDto) => runWithOwner(owner, () => toForm(formDto, undefined))),
  );
  const [formVersion] = createResource(() =>
    ApiService.getFormVersion(props.formId, props.formVersionId).then((formVersionDto) =>
      runWithOwner(owner, () => toFormVersion(formVersionDto, true)),
    ),
  );

  return (
    <FormVersionPreview
      ref={(ref) => (iframeRef = ref)}
      title={t(($) => $.CREATE_TASK_MENU_LABEL_PRE_FILL)}
      class="absolute inset-0 size-full"
      style={{ zoom: 0.7 }}
      form={form()}
      formVersion={formVersion()}
    />
  );
};

type VariablesTabProps = {
  formVariables: Record<string, VariableItem>;
  onUpdateValue: (uid: string, value: VariableValue) => void;
  onReset: (uid: string) => void;
};

const VariablesTab = (props: VariablesTabProps) => {
  const { t } = useTranslation();

  return (
    <div class="flex flex-col gap-y-4">
      <div class="flex flex-col gap-y-1">
        <Text as="h2" class="text-header-lg text-primary" text={t(($) => $.CREATE_TASK_VARIABLES_TITLE)} />
        <Text as="p" class="text-base-regular text-secondary" text={t(($) => $.CREATE_TASK_VARIABLES_DESCRIPTION)} />
      </div>
      <ul class="flex flex-col gap-y-4">
        <Index each={Object.values(props.formVariables)}>
          {(formVariable) => {
            const inputId = createUniqueId();
            return (
              <VariableItemCard
                variableItem={formVariable()}
                inputId={inputId}
                onReset={() => props.onReset(formVariable().variable.uid.toString())}
              >
                <Switch>
                  <Match when={formVariable().variable.type === "boolean"}>
                    <SingleSelect
                      ariaLabel={formVariable().variable.name}
                      value={formVariable().currentValue}
                      onSelect={(value) => props.onUpdateValue(formVariable().variable.uid.toString(), value)}
                      options={[
                        { variant: "text", value: true, label: t(($) => $.CREATE_TASK_VARIABLE_BOOLEAN_TRUE) },
                        { variant: "text", value: false, label: t(($) => $.CREATE_TASK_VARIABLE_BOOLEAN_FALSE) },
                      ]}
                      sameWidth
                      hasError={!isNil(formVariable().error)}
                    />
                  </Match>
                  <Match when={formVariable().variable.type === "number"}>
                    <CompactNumberInput
                      id={inputId}
                      value={formVariable().currentValue as number}
                      ariaLabel={formVariable().variable.name}
                      onBlur={(value) => props.onUpdateValue(formVariable().variable.uid.toString(), value)}
                      minValue={MAX_NUMBER_XL_NEGATIVE}
                      maxValue={MAX_NUMBER_XL}
                      allowDecimal
                      errorMessage={formVariable().error}
                    />
                  </Match>
                  <Match when={formVariable().variable.type === "date"}>
                    <CompactDateInput
                      id={inputId}
                      value={formVariable().currentValue as string}
                      ariaLabel={formVariable().variable.name}
                      onBlur={(event) =>
                        props.onUpdateValue(formVariable().variable.uid.toString(), event.target.value)
                      }
                      errorMessage={formVariable().error}
                    />
                  </Match>
                  <Match when={formVariable().variable.type === "datetime"}>
                    <CompactDateTimeInput
                      id={inputId}
                      value={formVariable().currentValue as string}
                      ariaLabel={formVariable().variable.name}
                      onBlur={(event) =>
                        props.onUpdateValue(formVariable().variable.uid.toString(), event.target.value)
                      }
                      errorMessage={formVariable().error}
                    />
                  </Match>
                  <Match when={formVariable().variable.type === "string"}>
                    <CompactTextInput
                      id={inputId}
                      value={formVariable().currentValue as string}
                      onBlur={(event) =>
                        props.onUpdateValue(formVariable().variable.uid.toString(), event.target.value)
                      }
                      errorMessage={formVariable().error}
                      ariaLabel={formVariable().variable.name}
                    />
                  </Match>
                </Switch>
              </VariableItemCard>
            );
          }}
        </Index>
      </ul>
    </div>
  );
};

type View = "recipients" | "planning" | "location" | "pre-fill" | "variables";
type SendTask = "IMMEDIATE" | "IN_ONE_HOUR" | "IN_TWO_HOURS" | "SCHEDULE";

type VariableValue = string | number | boolean | null | undefined;

type VariableItem = {
  definition: BaseVariableDefinition<BaseVariableType>;
  variable: VariableConfig;
  currentValue: VariableValue;
  error: string | undefined;
};

type MenuItem = {
  icon: IconName;
  label: TranslationKey;
  isOptional: boolean;
  view: View;
};

const buildMenuItems = (skipFormVariables: boolean): MenuItem[] => {
  const variablesMenuItem: MenuItem = {
    icon: "variable",
    label: keyFromSelector(($) => $.CREATE_TASK_MENU_LABEL_VARIABLES),
    isOptional: true,
    view: "variables",
  };

  const locationMenuItem: MenuItem = {
    icon: "locationMarker",
    label: keyFromSelector(($) => $.CREATE_TASK_MENU_LABEL_LOCATION),
    isOptional: true,
    view: "location",
  };

  return [
    {
      icon: "users",
      label: keyFromSelector(($) => $.CREATE_TASK_MENU_LABEL_RECIPIENTS),
      isOptional: false,
      view: "recipients",
    },
    {
      icon: "calendar",
      label: keyFromSelector(($) => $.CREATE_TASK_MENU_LABEL_PLANNING),
      isOptional: false,
      view: "planning",
    },
    ...(FeatureService.hasFeature("TASK_LOCATION") ? [locationMenuItem] : []),
    {
      icon: "documentText",
      label: keyFromSelector(($) => $.CREATE_TASK_MENU_LABEL_PRE_FILL),
      isOptional: true,
      view: "pre-fill",
    },
    ...(!skipFormVariables ? [variablesMenuItem] : []),
  ];
};

type SuccessModalProps = {
  schedule: string;
  sendTask: SendTask;
};

const SuccessModal = (props: SuccessModalProps) => {
  const { t } = useTranslation();
  const { close } = useCurrentModal();

  return (
    <Wizard
      title={t(($) => $.CREATE_TASK_MODAL_TITLE)}
      content={() => (
        <div class="flex h-full flex-col items-center justify-center text-center">
          <div class="flex size-20 flex-none shrink-0 items-center justify-center rounded-full bg-surface-status-subtle-success">
            <Icon name="checkCircle" size="2xl" type="solid" class="icon-status-success" />
          </div>
          <Text
            as="h3"
            text={t(($) => $.CREATE_TASK_SUCCESSFULLY_SCHEDULED)}
            class="mt-4 mb-1 max-w-85 text-header-lg text-primary"
          />
          <p class="max-w-85 text-medium-regular text-secondary">
            <Show
              when={condition(props.schedule, (s) => !isEmpty(s) && props.sendTask === "SCHEDULE")}
              fallback={t(($) => $.CREATE_TASK_SUCCESSFULLY_IMMEDIATE_DESCRIPTION)}
            >
              {(scheduleValue) => (
                <Translation
                  key={keyFromSelector(($) => $.CREATE_TASK_SUCCESSFULLY_SCHEDULED_DESCRIPTION)}
                  options={{ date: DEFAULT_DATE_TIME_FORMAT.format(new Date(scheduleValue())) }}
                  components={{
                    b: (props) => <>{props.children}</>,
                  }}
                />
              )}
            </Show>
          </p>
        </div>
      )}
      footer={{
        confirm: {
          label: keyFromSelector(($) => $.CLOSE_BUTTON),
          action: async () => close(),
          disabled: false,
        },
        showCloseButton: false,
      }}
    />
  );
};

export default CreateTaskWizard;
